import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
    size?: 'sm' | 'md' | 'lg';
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;
}

const Button: React.FC<ButtonProps> = ({
    children,
    variant = 'primary',
    size = 'md',
    isLoading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    disabled,
    className = '',
    ...props
}) => {
    const baseStyles = `
    inline-flex items-center justify-center font-semibold
    rounded-xl transition-all duration-300 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    active:scale-[0.98] transform-gpu
    touch-manipulation
  `;

    const variantStyles = {
        primary: `
      bg-gradient-to-r from-[#3B82F6] to-blue-600 text-white
      hover:from-blue-600 hover:to-blue-700
      focus:ring-[#3B82F6]
      shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30
      hover:-translate-y-0.5
    `,
        secondary: `
      bg-gray-100 text-gray-900
      hover:bg-gray-200
      focus:ring-gray-400
      shadow-sm hover:shadow-md
    `,
        outline: `
      border-2 border-[#3B82F6] text-[#3B82F6] bg-transparent
      hover:bg-[#3B82F6] hover:text-white
      focus:ring-[#3B82F6]
      shadow-sm hover:shadow-md
    `,
        ghost: `
      text-gray-700 bg-transparent
      hover:bg-gray-100/80
      focus:ring-gray-300
    `,
        danger: `
      bg-gradient-to-r from-red-600 to-red-700 text-white
      hover:from-red-700 hover:to-red-800
      focus:ring-red-500
      shadow-lg shadow-red-500/25 hover:shadow-xl hover:shadow-red-500/30
      hover:-translate-y-0.5
    `,
        success: `
      bg-gradient-to-r from-green-600 to-green-700 text-white
      hover:from-green-700 hover:to-green-800
      focus:ring-green-500
      shadow-lg shadow-green-500/25 hover:shadow-xl hover:shadow-green-500/30
      hover:-translate-y-0.5
    `,
    };

    const sizeStyles = {
        sm: 'px-4 py-2 text-sm min-h-[36px]',
        md: 'px-6 py-3 text-base min-h-[44px]',
        lg: 'px-8 py-4 text-lg min-h-[52px]',
    };

    const widthStyle = fullWidth ? 'w-full' : '';
    const iconSpacing = size === 'sm' ? 'gap-1.5' : size === 'lg' ? 'gap-3' : 'gap-2';

    return (
        <button
            className={`
        ${baseStyles}
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${widthStyle}
        ${className}
      `.replace(/\s+/g, ' ').trim()}
            disabled={disabled || isLoading}
            {...props}
        >
            {isLoading && (
                <svg
                    className={`animate-spin ${size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'}`}
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            )}
            {!isLoading && leftIcon && (
                <span className={`flex-shrink-0 ${iconSpacing.split(' ')[0]}`}>
                    {leftIcon}
                </span>
            )}
            <span className="whitespace-nowrap">{children}</span>
            {!isLoading && rightIcon && (
                <span className={`flex-shrink-0 ${iconSpacing.split(' ')[0]}`}>
                    {rightIcon}
                </span>
            )}
        </button>
    );
};

export default Button;

